import { ArrowRight, Shield, CheckCircle, TrendingUp, Award, Gem, Banknote } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import SEO from '../SEO';
import { pageSEO, faqSchemas } from '../../seoData';
import RelatedServices from '../RelatedServices';

const GoldBonds = () => {
  const [expandedFaq, setExpandedFaq] = useState(null);

  const faqs = [
    { question: "What are Sovereign Gold Bonds (SGB)?", answer: "Sovereign Gold Bonds are government securities denominated in grams of gold, issued by RBI on behalf of the Government of India. They offer an alternative to holding physical gold. Each unit represents 1 gram of gold. They combine gold price appreciation with 2.5% annual interest." },
    { question: "What are the tax benefits of SGB?", answer: "Capital gains on SGB are completely tax-free if held till maturity (8 years). The 2.5% annual interest is taxable as per your income slab. This makes SGB one of the most tax-efficient ways to invest in gold compared to physical gold or gold ETFs." },
    { question: "Can I sell SGB before maturity?", answer: "Yes, SGB can be traded on stock exchanges after the initial lock-in period. You can also exit after 5 years on interest payment dates. However, capital gains tax exemption only applies if held till 8-year maturity." },
    { question: "How much gold can I buy through SGB?", answer: "Minimum investment is 1 gram of gold. Maximum limit is 4 kg per individual per financial year. For trusts and similar entities, the limit is 20 kg. Price is linked to the prevailing gold price and often includes a discount for online applications." },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO {...pageSEO.goldBonds} faqJsonLd={faqSchemas.goldBonds} />

      <section className="relative bg-gradient-to-br from-yellow-50 via-white to-amber-50 pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <span className="inline-block bg-yellow-600 text-white px-4 py-2 rounded-full text-sm font-semibold tracking-wide mb-6">
              GOVERNMENT-BACKED GOLD
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Sovereign Gold Bonds
              <span className="block bg-gradient-to-r from-yellow-600 to-amber-600 bg-clip-text text-transparent">
                Smart Gold Investment
              </span>
            </h1>
            <p className="text-lg text-gray-600 mb-8 leading-relaxed max-w-3xl">
              Invest in gold without the hassles of physical storage. Sovereign Gold Bonds issued by 
              RBI offer gold price appreciation plus 2.5% annual interest, with tax-free capital gains 
              on maturity. A smarter, safer, and more rewarding way to add gold to your investment 
              portfolio with government-backed security.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact" className="bg-gradient-to-r from-yellow-600 to-amber-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:from-yellow-700 hover:to-amber-700 transition-all transform hover:scale-105 inline-flex items-center justify-center shadow-xl" data-testid="gold-cta">
                Invest in Gold Bonds <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
              <Link to="/services" className="border-2 border-yellow-600 text-yellow-700 hover:bg-yellow-600 hover:text-white px-8 py-4 rounded-xl font-bold text-lg transition-all inline-flex items-center justify-center">
                View All Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            <div><div className="text-3xl font-bold text-yellow-600 mb-1">RBI Issued</div><div className="text-gray-600 text-sm">Government Backed</div></div>
            <div><div className="text-3xl font-bold text-yellow-600 mb-1">2.5%</div><div className="text-gray-600 text-sm">Annual Interest</div></div>
            <div><div className="text-3xl font-bold text-yellow-600 mb-1">Tax-Free</div><div className="text-gray-600 text-sm">Capital Gains on Maturity</div></div>
            <div><div className="text-3xl font-bold text-yellow-600 mb-1">1 Gram</div><div className="text-gray-600 text-sm">Min Investment</div></div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-12 text-center">Why Choose Sovereign Gold Bonds?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: <Shield className="h-7 w-7 text-white" />, title: "Government Security", desc: "Issued by RBI on behalf of Government of India. Zero default risk. Your gold investment is backed by the sovereign guarantee of the Indian government." },
              { icon: <Banknote className="h-7 w-7 text-white" />, title: "Dual Returns", desc: "Earn gold price appreciation PLUS 2.5% annual interest paid semi-annually. No other gold investment gives you this additional income on top of gold returns." },
              { icon: <CheckCircle className="h-7 w-7 text-white" />, title: "Tax-Free on Maturity", desc: "Capital gains completely tax-free if held till 8-year maturity. The most tax-efficient way to invest in gold compared to physical gold, ETFs, or gold funds." },
              { icon: <Gem className="h-7 w-7 text-white" />, title: "No Storage Hassle", desc: "Held in demat form - no risk of theft, no locker charges, no purity concerns. Certificate of holding is issued digitally. Completely paperless investment." },
              { icon: <TrendingUp className="h-7 w-7 text-white" />, title: "Exchange Tradable", desc: "SGBs can be traded on stock exchanges, providing liquidity. You can exit after 5 years on interest payment dates or sell on exchange at prevailing market price." },
              { icon: <Award className="h-7 w-7 text-white" />, title: "Loan Collateral", desc: "SGBs can be used as collateral for loans from banks and NBFCs. The loan-to-value ratio is similar to physical gold, giving you liquidity without selling." },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all">
                <div className="w-14 h-14 bg-gradient-to-br from-yellow-500 to-amber-600 rounded-2xl flex items-center justify-center mb-5">{item.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-10 text-center">Gold Bond FAQs</h2>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-gray-50 rounded-xl overflow-hidden">
                <button onClick={() => setExpandedFaq(expandedFaq === i ? null : i)} className="w-full px-6 py-5 text-left flex justify-between items-center">
                  <span className="font-semibold text-gray-900">{faq.question}</span>
                  <span className="text-2xl text-yellow-600 ml-4">{expandedFaq === i ? '−' : '+'}</span>
                </button>
                {expandedFaq === i && <div className="px-6 pb-5"><p className="text-gray-700 leading-relaxed">{faq.answer}</p></div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      <RelatedServices links={['bonds', 'fd', 'commodity', 'nri', 'calculator']} />

      <section className="py-16 bg-gradient-to-r from-yellow-600 to-amber-600">
        <div className="max-w-4xl mx-auto px-4 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">Invest in Gold the Smart Way</h2>
          <p className="text-lg mb-8 text-yellow-100">Get notified about upcoming SGB tranches and invest with expert guidance.</p>
          <Link to="/contact" className="bg-white text-yellow-700 px-8 py-4 rounded-xl font-bold text-lg hover:bg-gray-100 transition-all inline-flex items-center">
            Get Started <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default GoldBonds;
